import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ChatWidget from './ChatWidget';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Sheet, SheetContent } from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import {
  LayoutDashboard, Building2, FileInput, FileMinus, Users, Receipt,
  PenLine, BookOpen, BookText, Scale, ArrowLeftRight,
  Landmark, Send, Wifi, Link2, Mail, Calculator,
  BarChart3, PieChart, CheckCircle2,
  Settings, GitBranch, Shield, Zap,
  Wallet, Briefcase, Coins, CalendarClock,
  LogOut, Menu, Bell, Moon, Sun, Crown, HelpCircle,
  Loader2, Trash2, CheckCheck, Clock, X, ChevronRight
} from 'lucide-react';

/* ============================================================
   NAV STRUCTURE — Grouped by business domain
   ============================================================ */
const navSections = [
  {
    items: [
      { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, module: 'dashboard' },
    ],
  },
  {
    label: 'ACHATS',
    items: [
      { path: '/suppliers', label: 'Fournisseurs', icon: Building2, module: 'suppliers' },
      { path: '/invoices', label: 'Factures fournisseurs', icon: FileInput, module: 'invoices' },
      { path: '/credit-notes', label: 'Avoirs fournisseurs', icon: FileMinus, module: 'invoices' },
    ],
  },
  {
    label: 'VENTES',
    items: [
      { path: '/clients', label: 'Clients', icon: Users, module: 'invoices' },
      { path: '/factures-clients', label: 'Factures clients', icon: Receipt, module: 'invoices' },
      { path: '/prestations', label: 'Prestations', icon: Receipt, module: 'invoices' },
    ],
  },
  {
    label: 'COMPTABILITÉ',
    items: [
      { path: '/ecritures', label: 'Écritures', icon: PenLine, module: 'admin' },
      { path: '/plan-comptable', label: 'Plan comptable', icon: BookOpen, module: 'admin' },
      { path: '/grand-livre', label: 'Grand livre', icon: BookText, module: 'admin' },
      { path: '/balance', label: 'Balance', icon: Scale, module: 'admin' },
      { path: '/rapprochement', label: 'Rapprochement bancaire', icon: ArrowLeftRight, module: 'admin' },
      { path: '/tva', label: 'TVA', icon: Calculator, module: 'admin' },
      { path: '/conformite', label: 'Conformité', icon: Shield, module: 'admin' },
      { path: '/e-invoicing', label: 'E-Invoicing', icon: Zap, module: 'admin' },
    ],
  },
  {
    label: 'AUTOMATISATION',
    items: [
      { path: '/connexion-bancaire', label: 'Connexion bancaire', icon: Wifi, module: 'admin' },
      { path: '/lettrage', label: 'Lettrage automatique', icon: Link2, module: 'admin' },
      { path: '/collecte-email', label: 'Collecte factures', icon: Mail, module: 'admin' },
    ],
  },
  {
    label: 'TRÉSORERIE',
    items: [
      { path: '/tresorerie', label: 'Prévisions cash-flow', icon: Wallet, module: 'admin' },
      { path: '/bank-accounts', label: 'Comptes bancaires', icon: Landmark, module: 'admin' },
      { path: '/sepa-transfers', label: 'Virements SEPA', icon: Send, module: 'invoices' },
      { path: '/scheduled-payments', label: 'Paiements programmés', icon: CalendarClock, module: 'invoices' },
      { path: '/devises', label: 'Multi-devises', icon: Coins, module: 'admin' },
    ],
  },
  {
    label: 'PILOTAGE',
    items: [
      { path: '/reports', label: 'Rapports', icon: BarChart3, module: 'reports' },
      { path: '/analytical', label: 'Analytique', icon: PieChart, module: 'analytical' },
      { path: '/validation', label: 'Validation', icon: CheckCircle2, module: 'validation', showBadge: true },
      { path: '/notes-frais', label: 'Notes de frais', icon: Receipt, module: 'invoices' },
    ],
  },
  {
    label: 'ADMINISTRATION',
    items: [
      { path: '/admin', label: 'Paramètres', icon: Settings, module: 'admin' },
      { path: '/subscription', label: 'Abonnement', icon: Landmark, module: null },
      { path: '/api-webhooks', label: 'API & Webhooks', icon: Link2, module: 'admin' },
    ],
  },
];

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasModuleAccess } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  /* ---------- Notifications ---------- */
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setNotifications(await res.json());
    } catch (e) {}
  }, [user]);

  const fetchNotifCount = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/count`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setNotifCount(d.count); }
    } catch (e) {}
  }, [user]);

  const fetchPendingCount = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/pending-count`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setPendingCount(d.count || 0); }
    } catch (e) {}
  }, [user]);

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${id}/read`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, lu: true } : n));
      setNotifCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/read-all`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` } });
      setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
      setNotifCount(0);
    } catch (e) {}
  };

  const deleteNotification = async (id, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const notif = notifications.find(n => n.id === id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      if (notif && !notif.lu) setNotifCount(prev => Math.max(0, prev - 1));
    } catch (e) {}
  };

  useEffect(() => {
    fetchNotifCount(); fetchPendingCount();
    const i = setInterval(() => { fetchNotifCount(); fetchPendingCount(); }, 30000);
    return () => clearInterval(i);
  }, [fetchNotifCount, fetchPendingCount]);

  useEffect(() => {
    if (notifOpen) { setNotifLoading(true); fetchNotifications().finally(() => setNotifLoading(false)); }
  }, [notifOpen, fetchNotifications]);

  const formatNotifDate = (d) => {
    const diff = Math.floor((Date.now() - new Date(d)) / 1000);
    if (diff < 60) return "À l'instant";
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;
    return new Date(d).toLocaleDateString('fr-FR');
  };

  const toggleDarkMode = () => { setDarkMode(!darkMode); document.documentElement.classList.toggle('dark'); };
  const handleLogout = () => { logout(); navigate('/login'); };

  /* ---------- Nav helpers ---------- */
  const hasAccess = (item) => !item.module || hasModuleAccess(item.module);

  const getPageTitle = () => {
    for (const s of navSections) {
      for (const item of s.items) {
        if (item.path === location.pathname) return item.label;
      }
    }
    return 'Tableau de bord';
  };

  /* ---------- Nav item component ---------- */
  const NavItem = ({ item, mobile }) => {
    if (!hasAccess(item)) return null;
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    return (
      <Link
        to={item.path}
        onClick={() => mobile && setSidebarOpen(false)}
        className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150 relative ${
          isActive
            ? 'bg-teal-50 text-[#0F5156] font-medium dark:bg-[#0F5156]/20 dark:text-[#69AFAF]'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white'
        }`}
      >
        {isActive && <div className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r-full bg-[#0F5156] dark:bg-[#69AFAF]" />}
        <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.5} />
        <span className="truncate">{item.label}</span>
        {item.showBadge && pendingCount > 0 && (
          <Badge className="ml-auto bg-red-500 text-white text-[10px] px-1.5 py-0 min-w-[18px] h-[18px] flex items-center justify-center rounded-full">
            {pendingCount}
          </Badge>
        )}
      </Link>
    );
  };

  /* ---------- Sidebar content ---------- */
  const SidebarContent = ({ mobile = false }) => {
    const { getTrialInfo } = useAuth();
    const trialInfo = typeof getTrialInfo === 'function' ? getTrialInfo() : null;

    return (
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="w-8 h-8 rounded-lg bg-[#0F5156] flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-[15px] text-slate-900 dark:text-white tracking-tight">Nexus Compta</span>
        </div>

        {/* Trial */}
        {trialInfo && (
          <div className="mx-3 mt-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <Clock className="w-3.5 h-3.5" />
              <span className="text-xs font-medium">Période d'essai — {trialInfo.daysRemaining}j restant(s)</span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-5">
            {navSections.map((section, si) => {
              const visibleItems = section.items.filter(hasAccess);
              if (visibleItems.length === 0) return null;

              return (
                <div key={si}>
                  {section.label && (
                    <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-widest uppercase text-slate-400 dark:text-slate-500">
                      {section.label}
                    </p>
                  )}
                  <div className="space-y-0.5">
                    {visibleItems.map(item => (
                      <NavItem key={item.path} item={item} mobile={mobile} />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Expert-comptable */}
            {(user?.role === 'expert_comptable' || user?.role === 'super_admin') && (
              <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                <NavItem item={{ path: '/espace-expert', label: 'Espace expert-comptable', icon: Briefcase }} mobile={mobile} />
              </div>
            )}

            {/* Holding */}
            {(user?.role === 'holding' || user?.role === 'super_admin') && (
              <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                <NavItem item={{ path: '/espace-holding', label: 'Espace holding', icon: Landmark }} mobile={mobile} />
              </div>
            )}

            {/* Help */}
            <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
              <NavItem item={{ path: '/help', label: 'Aide', icon: HelpCircle }} mobile={mobile} />
            </div>

            {/* Super Admin */}
            {user?.role === 'super_admin' && (
              <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
                <NavItem item={{ path: '/superadmin', label: 'Super Admin', icon: Crown }} mobile={mobile} />
              </div>
            )}
          </nav>
        </ScrollArea>

        {/* User card */}
        <div className="px-3 py-3 border-t border-slate-100 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-2.5 px-2">
            <Avatar className="w-8 h-8">
              <AvatarFallback className="bg-[#0F5156] text-white text-xs font-medium">
                {user?.prenom?.[0]}{user?.nom?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.prenom} {user?.nom}</p>
              <p className="text-[11px] text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ---------- Render ---------- */
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex md:w-60 md:flex-col md:fixed md:inset-y-0 bg-white dark:bg-slate-800 border-r border-slate-100 dark:border-slate-700">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <SidebarContent mobile />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className="md:pl-60">
        {/* Top bar */}
        <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center justify-between px-4 md:px-8 h-14">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSidebarOpen(true)}>
                <Menu className="w-5 h-5" />
              </Button>
              <h2 className="text-[15px] font-semibold text-slate-900 dark:text-white hidden sm:block">{getPageTitle()}</h2>
            </div>

            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="w-8 h-8">
                {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </Button>

              {/* Notifications */}
              <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative w-8 h-8">
                    <Bell className="w-4 h-4" />
                    {notifCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] flex items-center justify-center bg-red-500 text-white text-[10px] font-medium rounded-full px-1">
                        {notifCount > 99 ? '99+' : notifCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 p-0">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                    <h3 className="font-semibold text-sm">Notifications</h3>
                    {notifCount > 0 && (
                      <button onClick={markAllAsRead} className="text-xs text-[#0F5156] hover:underline flex items-center gap-1">
                        <CheckCheck className="w-3 h-3" /> Tout marquer lu
                      </button>
                    )}
                  </div>
                  <ScrollArea className="max-h-[360px]">
                    {notifLoading ? (
                      <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                    ) : notifications.length === 0 ? (
                      <div className="flex flex-col items-center py-8 text-slate-400">
                        <Bell className="w-8 h-8 mb-2 opacity-40" /><p className="text-sm">Aucune notification</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-50 dark:divide-slate-800">
                        {notifications.map(n => (
                          <div key={n.id}
                            className={`px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer group ${!n.lu ? 'bg-blue-50/40' : ''}`}
                            onClick={() => { if (!n.lu) markAsRead(n.id); if (n.lien) { navigate(n.lien); setNotifOpen(false); } }}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className={`w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0 ${!n.lu ? 'bg-[#0F5156]' : 'bg-transparent'}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!n.lu ? 'font-semibold' : ''} text-slate-900 dark:text-white`}>{n.titre}</p>
                                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</p>
                                <p className="text-[11px] text-slate-400 mt-1">{formatNotifDate(n.created_at)}</p>
                              </div>
                              <button onClick={(e) => deleteNotification(n.id, e)}
                                className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-all">
                                <Trash2 className="w-3.5 h-3.5 text-slate-400" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 h-8 px-2">
                    <Avatar className="w-7 h-7">
                      <AvatarFallback className="bg-[#0F5156] text-white text-[10px] font-medium">
                        {user?.prenom?.[0]}{user?.nom?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">{user?.prenom}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.prenom} {user?.nom}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}><Settings className="w-4 h-4 mr-2" />Paramètres</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/subscription')}><Crown className="w-4 h-4 mr-2" />Abonnement</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600"><LogOut className="w-4 h-4 mr-2" />Déconnexion</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-4 md:p-8 pb-24 md:pb-8">
          {children}
        </main>
      </div>

      {/* In-app chat widget (M5.3) */}
      <ChatWidget />
    </div>
  );
};

export default Layout;
